import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  CheckGlyph,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { Brand } from './kit';

const IMG = 'https://media.easy-peasy.ai/landing/workshops/trainers';

/** `src` is the full certificate for the enlarged view, `thumb` the card copy. */
type Certificate = { name: string; src: string; thumb: string };

type Trainer = {
  name: string;
  role: string;
  avatar: string;
  bio: string;
  highlights: string[];
  certificates: Certificate[];
};

const TRAINERS: Trainer[] = [
  {
    name: 'Marianna Olefyrenko',
    role: 'CEO and Co-Founder',
    avatar: `${IMG}/marianna.webp`,
    bio: 'Marianna has worked in IT for more than 15 years, across AI, software engineering, data analytics and UX. She turns complex AI into practical steps for business owners.',
    highlights: [
      'WSQ Advanced Certificate in Learning and Performance (ACLP)',
      'Certified trainer, 5+ years training business owners',
      'Professional Scrum Product Owner (PSPO)',
    ],
    certificates: [
      {
        name: 'ACLP Certification',
        src: `${IMG}/aclp-certificate.webp`,
        thumb: `${IMG}/aclp-certificate-thumb.webp`,
      },
      {
        name: 'ISCA Member',
        src: `${IMG}/isca-membership.webp`,
        thumb: `${IMG}/isca-membership-thumb.webp`,
      },
      {
        name: 'NTU AI Certification',
        src: `${IMG}/ntu-ai-certificate.webp`,
        thumb: `${IMG}/ntu-ai-certificate-thumb.webp`,
      },
      {
        name: 'AWS Generative AI Essentials',
        src: `${IMG}/aws-generative-ai-essentials.webp`,
        thumb: `${IMG}/aws-generative-ai-essentials.webp`,
      },
    ],
  },
  {
    name: 'Dmytro Olefyrenko',
    role: 'CTO and Co-Founder',
    avatar: `${IMG}/dmytro.webp`,
    bio: 'Dmytro has 16+ years in software engineering, from serverless and AWS to AI agents. He builds the Easy-Peasy.AI platform and shows how it works under the hood.',
    highlights: [
      'AWS Certified Solutions Architect and Developer (Associate)',
      'Google Mobile Web Specialist',
      'Google Developer Group member since 2018',
    ],
    certificates: [
      {
        name: 'AWS Certified Solutions Architect – Associate',
        src: `${IMG}/aws-solutions-architect-associate.webp`,
        thumb: `${IMG}/aws-solutions-architect-associate.webp`,
      },
      {
        name: 'Google Mobile Web Specialist',
        src: `${IMG}/google-mobile-web-specialist.webp`,
        thumb: `${IMG}/google-mobile-web-specialist-thumb.webp`,
      },
    ],
  },
];

export default function WorkshopsTrainers() {
  const { ref, state } = useReveal<HTMLDivElement>(0.15);
  const [open, setOpen] = useState<Certificate | null>(null);

  return (
    <section className='pb-24 sm:pb-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='Taught by the founders.'
          tail='The people who build the product.'
          lead={
            <>
              Our workshops are led by Marianna and Dmytro, who started{' '}
              <Brand /> in 2022 and still build it.
            </>
          }
        />

        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(styles.reveal, 'mt-14 grid gap-4 lg:grid-cols-2')}
        >
          {TRAINERS.map((trainer, index) => (
            <article
              key={trainer.name}
              className={clsxm(
                styles.rv,
                'flex flex-col rounded-[26px] p-7 sm:p-9',
                tone.surface,
                tone.line,
              )}
              style={delay(index * 120)}
            >
              <div className='flex items-center gap-5'>
                <Image
                  src={trainer.avatar}
                  alt={trainer.name}
                  width={80}
                  height={80}
                  unoptimized
                  className='h-20 w-20 flex-none rounded-[22px] bg-[#f3f3f8] object-cover ring-1 ring-[#0f0f28]/[0.07] dark:ring-white/10'
                />
                <div className='min-w-0'>
                  <h3
                    className={clsxm(
                      'text-2xl font-semibold tracking-[-0.02em]',
                      tone.ink,
                    )}
                  >
                    {trainer.name}
                  </h3>
                  <p className='mt-1 text-[15px] font-medium text-primary-600 dark:text-primary-400'>
                    {trainer.role}
                  </p>
                </div>
              </div>

              <p className={clsxm('mt-6 text-[16px] leading-7', tone.body)}>
                {trainer.bio}
              </p>

              <ul className='mt-6 space-y-3'>
                {trainer.highlights.map((item) => (
                  <li
                    key={item}
                    className={clsxm(
                      'flex gap-2.5 text-[15px] leading-6',
                      tone.body,
                    )}
                  >
                    <CheckGlyph className='mt-0.5 h-[18px] w-[18px] flex-none' />
                    {item}
                  </li>
                ))}
              </ul>

              <div className='mt-auto pt-8'>
                <p className={clsxm('text-[13px] font-medium', tone.quiet)}>
                  Certificates
                </p>
                <ul className='mt-3 flex flex-wrap gap-3'>
                  {trainer.certificates.map((certificate) => (
                    <li key={certificate.name}>
                      <button
                        type='button'
                        onClick={() => setOpen(certificate)}
                        aria-label={`Enlarge ${certificate.name}`}
                        className='group relative flex h-24 w-28 items-center justify-center overflow-hidden rounded-xl bg-[#f3f3f8] p-2 ring-1 ring-[#0f0f28]/[0.06] transition hover:ring-primary-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-white/[0.06] dark:ring-white/[0.08]'
                      >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={certificate.thumb}
                          alt=''
                          loading='lazy'
                          decoding='async'
                          className='max-h-full max-w-full object-contain transition-transform duration-200 group-hover:scale-105'
                        />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <Link
          href='/about-us#founders'
          className={clsxm(
            'mt-8 inline-flex items-center gap-1.5 text-[15px] font-semibold underline-offset-4 hover:underline',
            tone.ink,
          )}
        >
          More about the founders
          <ArrowGlyph className='h-3.5 w-3.5' />
        </Link>
      </div>

      {open ? (
        <CertificateViewer certificate={open} onClose={() => setOpen(null)} />
      ) : null}
    </section>
  );
}

function CertificateViewer({
  certificate,
  onClose,
}: {
  certificate: Certificate;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [onClose]);

  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      aria-label={certificate.name}
      onClick={onClose}
      className='fixed inset-0 z-[1200] flex items-center justify-center bg-[#0d0c1f]/85 p-4 backdrop-blur-sm sm:p-10'
    >
      <button
        ref={closeRef}
        type='button'
        onClick={onClose}
        aria-label='Close'
        className='absolute end-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white'
      >
        <svg viewBox='0 0 24 24' className='h-5 w-5' aria-hidden='true'>
          <path
            d='M6 6l12 12M18 6 6 18'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          />
        </svg>
      </button>
      <figure
        onClick={(event) => event.stopPropagation()}
        className='flex max-h-full max-w-4xl flex-col items-center'
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={certificate.src}
          alt={certificate.name}
          className='max-h-[80vh] w-auto max-w-full rounded-xl bg-white object-contain shadow-2xl'
        />
        <figcaption className='mt-4 text-center text-sm font-medium text-white'>
          {certificate.name}
        </figcaption>
      </figure>
    </div>,
    document.body,
  );
}
